import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, FileText, Gauge, Globe, Search, ShieldCheck } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

// Checks the auditor will run once it ships. Nothing here is scored yet —
// the page must not imply a result until the engine is real.
const PLANNED_CHECKS = [
  {
    icon: ShieldCheck,
    name: 'SSL / TLS certificate',
    detail: 'Issuer, real expiry date, chain validity and protocol version.',
  },
  {
    icon: Gauge,
    name: 'Load performance',
    detail: 'Measured TTFB, total transfer size and render-blocking resources.',
  },
  {
    icon: ShieldCheck,
    name: 'HTTP security headers',
    detail: 'HSTS, Content-Security-Policy, X-Content-Type-Options, Referrer-Policy.',
  },
  {
    icon: Search,
    name: 'SEO & OpenGraph tags',
    detail: 'Title, meta description, canonical URL and social preview tags.',
  },
  {
    icon: FileText,
    name: 'Image & asset delivery',
    detail: 'Oversized images, missing modern formats (WebP/AVIF), cache headers.',
  },
];

export const Auditor = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Website Security & SEO Auditor — Coming Soon | BikerTechie',
    description:
      'Our automated SSL, security-header and SEO auditing tool is in development. In the meantime, request a free manual audit of your website from the BikerTechie team.',
    path: '/audit',
  });

  return (
    <div className="min-h-screen bg-[#030712] pt-24 text-white" data-testid="auditor-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Title */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 text-amber-300 text-xs font-semibold uppercase tracking-wider">
            <Clock size={13} />
            Coming Soon
          </span>
          <h1 className="text-4xl font-extrabold text-white mt-4 mb-2">
            Security &amp; SEO{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
              Auditor
            </span>
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto text-sm">
            We're building a one-click auditor that inspects your live site and reports what it
            actually finds — real certificate data, measured load times and your true response
            headers. It isn't ready yet, so we'd rather not show you a number we can't stand behind.
          </p>
        </div>

        {/* Primary CTA — a manual audit we can genuinely deliver today */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/30 border border-white/5 rounded-3xl p-6 md:p-8 backdrop-blur-xl mb-8 text-center"
        >
          <div className="w-12 h-12 rounded-2xl bg-violet-600/10 border border-violet-500/30 flex items-center justify-center mx-auto mb-4">
            <Globe size={20} className="text-violet-300" />
          </div>
          <h2 className="text-lg font-bold mb-2">Want the audit now? We'll do it by hand.</h2>
          <p className="text-slate-400 text-sm max-w-lg mx-auto mb-6">
            Send us your URL and one of our engineers will run the same checks manually and send you
            a written report — free, no obligation. Typical turnaround is two working days.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="bg-gradient-to-r from-violet-600 to-cyan-600 font-bold px-8 py-3.5 rounded-2xl text-sm inline-flex items-center justify-center gap-2 shadow-lg shadow-violet-500/20"
            >
              Request a free manual audit
              <ArrowRight size={16} />
            </button>
            <a
              href="mailto:contact@bikertechie.com?subject=Website%20audit%20request"
              className="px-8 py-3.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all"
            >
              Email us instead
            </a>
          </div>
        </motion.div>

        {/* What the tool will check — clearly labelled as not-yet-live */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900/20 border border-white/5 p-6 md:p-8 rounded-3xl backdrop-blur-md"
        >
          <div className="flex items-baseline justify-between mb-6 gap-4 flex-wrap">
            <h3 className="text-sm font-semibold uppercase tracking-wider">
              What the auditor will check
            </h3>
            <span className="text-[10px] text-amber-300/80 uppercase tracking-wider font-semibold">
              Not live yet
            </span>
          </div>

          <div className="space-y-4">
            {PLANNED_CHECKS.map((check) => {
              const Icon = check.icon;
              return (
                <div
                  key={check.name}
                  className="p-4 rounded-2xl bg-slate-950/50 border border-white/5 flex items-start gap-3"
                >
                  <div className="mt-0.5 flex-shrink-0 text-slate-500">
                    <Icon size={18} />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-200">{check.name}</h4>
                    <p className="text-[10px] text-slate-500 mt-1 leading-snug">{check.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-[11px] text-slate-500 mt-6 leading-relaxed">
            In the meantime, our engineering team covers all of the above as part of every cloud and
            web engagement — see{' '}
            <button
              onClick={() => navigate('/services')}
              className="text-violet-300 hover:text-violet-200 underline underline-offset-2"
            >
              our services
            </button>{' '}
            or read how we approached it for{' '}
            <button
              onClick={() => navigate('/case-studies')}
              className="text-violet-300 hover:text-violet-200 underline underline-offset-2"
            >
              previous clients
            </button>
            .
          </p>
        </motion.div>

      </div>
    </div>
  );
};

export default Auditor;
